import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import { tokens } from "../../../design/theme.next";
import { inDark } from "../../../design/colorScheme";
import WordmarkDot from "./WordmarkDot";

/**
 * One way to wait, everywhere. Five tiers:
 *
 *   <PageLoader />                       page or route load: the wordmark, after 300ms
 *   <Skeleton w="60%" h={14} />          a section's content, shaped like it
 *   <BusyDots busy verb="Saving">Save</BusyDots>   a button or inline action
 *   <HugoThinking lines={[...]} />       Hugo doing the work (grading, writing)
 *     size="inline"  a face and a line, inside text or a card
 *     size="section" a card with the face beside the line (the default)
 *     size="page"    the main wait on a screen: a large face, centred, the line under it
 *   <ProgressBar value={62} label="Uploading your CV" />   a known percentage
 *
 * Every tier holds still with reduced motion and follows dark mode.
 */

const a4 = tokens.a4;
const c = a4.color;
const soft = "cubic-bezier(.4,0,.2,1)";
const still = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };

// The visible content area under the app header, above the phone tab bar.
export const CONTENT_HEIGHT = { xs: "calc(100dvh - 220px)", md: "calc(100dvh - 160px)" };
// Optical centre: a little above the true middle.
const LIFT = { pb: { xs: "8vh", md: "10vh" } };
export const FACE_SIZE = { inline: 28, section: 44, page: { xs: 76, md: 92 } };

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: none; }
`;
const sheen = keyframes`
  from { background-position: 100% 0; }
  to   { background-position: 0 0; }
`;
const wave = keyframes`
  0%, 60%, 100% { transform: none; opacity: .4; }
  30%           { transform: scale(1.3); opacity: 1; }
`;
const breathe = keyframes`
  0%, 100% { transform: none; opacity: 1; }
  45%      { transform: scale(1.4); opacity: .55; }
`;
const glance = keyframes`
  0%, 90%, 100% { transform: scaleY(1); }
  94%           { transform: scaleY(.12); }
`;
const drawSmile = keyframes`
  from { stroke-dashoffset: 9; }
  to   { stroke-dashoffset: 0; }
`;

// Content that replaces a loader fades in, so nothing jumps into place.
export const contentInSx = { animation: `${fadeIn} 320ms ${a4.ease} both`, ...still };

export const reducedMotion = () => {
  try {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch (_e) {
    return false;
  }
};

// Renders nothing until `delay` has passed, so quick loads never flash.
export function useDelayed(delay) {
  const [shown, setShown] = useState(delay <= 0);
  useEffect(() => {
    if (delay <= 0) return undefined;
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return shown;
}

/** (a) Page or route load: "Hugo." centred, its full stop breathing. */
export function PageLoader({ delay = 300, fullScreen = true, label = "Loading", minHeight = CONTENT_HEIGHT, sx }) {
  const shown = useDelayed(delay);
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label}
      sx={{
        display: "grid",
        placeItems: "center",
        ...(fullScreen
          ? { position: "fixed", inset: 0, zIndex: tokens.zIndex?.modal || 1300, background: `${c.glow}, ${c.bg}` }
          : { minHeight }),
        ...LIFT,
        ...sx,
      }}
    >
      {shown && (
        <Box
          aria-hidden="true"
          data-testid="page-loader-mark"
          sx={{
            fontFamily: tokens.fontFamily.sans,
            fontWeight: 800,
            fontSize: { xs: "44px", md: "56px" },
            letterSpacing: "-0.065em",
            lineHeight: 1,
            color: c.ink,
            animation: `${fadeIn} 240ms ${a4.ease} both`,
            ...still,
          }}
        >
          Hugo
          <WordmarkDot color={c.violet} settleIn={false} thinking />
        </Box>
      )}
    </Box>
  );
}

PageLoader.propTypes = {
  delay: PropTypes.number,
  fullScreen: PropTypes.bool,
  label: PropTypes.string,
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  sx: PropTypes.object,
};

/** (b) A placeholder block with a slow, soft sheen. Compose them into the content's shape. */
export function Skeleton({ w = "100%", h = 12, r = 8, sx }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: w,
        height: h,
        borderRadius: typeof r === "number" ? `${r}px` : r,
        flex: "none",
        background: "linear-gradient(100deg, #F1EEF8 30%, #FAF8FE 50%, #F1EEF8 70%)",
        ...inDark({ background: "linear-gradient(100deg, #1E1929 30%, #2A2338 50%, #1E1929 70%)" }),
        backgroundSize: "300% 100%",
        animation: `${sheen} 1.6s ${soft} infinite`,
        ...still,
        ...sx,
      }}
    />
  );
}

Skeleton.propTypes = {
  w: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  h: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  r: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
};

/** A card-shaped skeleton: a logo square, a title, a meta line and chips. */
export function SkeletonCard({ lines = 2, chips = 2, logo = true, sx }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        bgcolor: c.card,
        borderRadius: "18px",
        boxShadow: `0 0 0 1px ${c.line}`,
        p: "16px",
        display: "grid",
        gridTemplateColumns: logo ? "40px minmax(0,1fr)" : "minmax(0,1fr)",
        gap: "8px 12px",
        ...sx,
      }}
    >
      {logo && <Skeleton w={40} h={40} r={12} sx={{ gridRow: `span ${lines}` }} />}
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} w={i === 0 ? "72%" : "46%"} h={i === 0 ? 14 : 12} />
      ))}
      {chips > 0 && (
        <Box sx={{ gridColumn: logo ? 2 : 1, display: "flex", gap: "6px", mt: "4px" }}>
          {Array.from({ length: chips }).map((_, i) => (
            <Skeleton key={i} w={i === chips - 1 ? 48 : 64} h={22} r={999} />
          ))}
        </Box>
      )}
    </Box>
  );
}

SkeletonCard.propTypes = { lines: PropTypes.number, chips: PropTypes.number, logo: PropTypes.bool, sx: PropTypes.object };

/** Lines of text still to come. */
export function SkeletonText({ lines = 3, sx }) {
  return (
    <Box aria-hidden="true" sx={{ display: "grid", gap: "9px", ...sx }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} w={i === lines - 1 ? "62%" : i % 2 ? "88%" : "96%"} />
      ))}
    </Box>
  );
}

SkeletonText.propTypes = { lines: PropTypes.number, sx: PropTypes.object };

/** Three small dots, or one breathing dot, in the text colour. */
export function Dots({ one = false, sx }) {
  if (one) {
    return (
      <Box
        component="span"
        aria-hidden="true"
        sx={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor", flex: "none", animation: `${breathe} 900ms ${soft} infinite`, ...still, ...sx }}
      />
    );
  }
  return (
    <Box component="span" aria-hidden="true" sx={{ display: "inline-flex", gap: "3px", alignItems: "center", height: 10, flex: "none", ...sx }}>
      {[0, 140, 280].map((d) => (
        <Box
          key={d}
          component="span"
          sx={{
            width: 4.5,
            height: 4.5,
            borderRadius: "50%",
            bgcolor: "currentColor",
            animation: `${wave} 1100ms ${soft} ${d}ms infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.7 },
          }}
        />
      ))}
    </Box>
  );
}

Dots.propTypes = { one: PropTypes.bool, sx: PropTypes.object };

/**
 * (c) Busy inside a button. The label becomes a verb with dots, and the
 * button keeps its width: both labels share one grid cell.
 */
export function BusyDots({ busy = false, verb, one = false, children }) {
  const busyText = verb || children;
  return (
    <Box component="span" sx={{ display: "inline-grid", alignItems: "center", justifyItems: "center", "& > *": { gridArea: "1 / 1" } }}>
      <Box component="span" aria-hidden={busy || undefined} sx={{ display: "inline-flex", alignItems: "center", gap: "9px", visibility: busy ? "hidden" : "visible" }}>
        {children}
      </Box>
      <Box component="span" aria-hidden={!busy || undefined} sx={{ display: "inline-flex", alignItems: "center", gap: "9px", visibility: busy ? "visible" : "hidden" }}>
        {busyText}
        <Dots one={one} />
      </Box>
    </Box>
  );
}

BusyDots.propTypes = { busy: PropTypes.bool, verb: PropTypes.node, one: PropTypes.bool, children: PropTypes.node };

/**
 * Hugo's small face. While `thinking` the eyes glance up and blink and the
 * smile goes level; when thinking stops the smile draws back in.
 */
export function HugoFace({ thinking = false, size = 28, sx }) {
  const [landed, setLanded] = useState(0);
  const wasThinking = useRef(thinking);
  useEffect(() => {
    if (wasThinking.current && !thinking) setLanded((n) => n + 1);
    wasThinking.current = thinking;
  }, [thinking]);

  return (
    <Box
      aria-hidden="true"
      data-thinking={thinking ? "true" : undefined}
      sx={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        bgcolor: c.card,
        boxShadow: `0 0 0 1px ${c.lineStrong}`,
        ...(typeof size === "number" && size >= 56 ? { boxShadow: `0 0 0 1px ${c.lineStrong}, 0 18px 40px -22px rgba(76, 47, 196, .45)` } : {}),
        color: c.violetInk,
        "& svg": { width: "100%", height: "100%", overflow: "visible" },
        "& .eyes": { transition: `transform 420ms ${a4.ease}`, transform: thinking ? "translate(1.3px, -1.4px)" : "none" },
        "& .eye": {
          transformBox: "fill-box",
          transformOrigin: "center",
          transition: `transform 380ms ${a4.ease}`,
          animation: thinking ? `${glance} 3200ms ${soft} 500ms infinite` : "none",
        },
        "& .smile, & .flat": { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", transition: `opacity 260ms ${a4.ease}` },
        "& .smile": { opacity: thinking ? 0 : 1, strokeDasharray: 9, animation: landed ? `${drawSmile} 520ms ${a4.ease} both` : "none" },
        "& .flat": { opacity: thinking ? 1 : 0 },
        "@media (prefers-reduced-motion: reduce)": { "& *": { animation: "none !important", transition: "none !important" } },
        ...sx,
      }}
    >
      <svg viewBox="0 0 28 28">
        <g className="eyes">
          <rect className="eye" x="9.2" y="9.6" width="2.6" height="4.4" rx="1.3" fill="currentColor" />
          <rect className="eye" x="16.2" y="9.6" width="2.6" height="4.4" rx="1.3" fill="currentColor" />
        </g>
        <path key={landed} className="smile" d="M10 17.4q4 3.4 8 0" />
        <path className="flat" d="M11 18.6h6" />
      </svg>
    </Box>
  );
}

HugoFace.propTypes = { thinking: PropTypes.bool, size: PropTypes.oneOfType([PropTypes.number, PropTypes.object]), sx: PropTypes.object };

// Steps through `lines` every `every` ms, stopping on the last one.
export function useRotatingLine(lines, every = 2400) {
  const list = Array.isArray(lines) ? lines : [lines];
  const [i, setI] = useState(0);
  const key = list.join("|");
  useEffect(() => {
    setI(0);
    if (list.length < 2) return undefined;
    const t = setInterval(() => setI((n) => Math.min(n + 1, list.length - 1)), every);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, every]);
  return list[Math.min(i, list.length - 1)];
}

/**
 * (d) Hugo working on something long. The face thinks beside one short
 * line saying what is happening, which moves on through `lines`. Pass
 * `step` and `total`, or `progress` (0-100), when the progress is known.
 */
export function HugoThinking({ lines, status, step, total, progress, sub, compact = false, size, every, minHeight, sx }) {
  const preset = size || (compact ? "inline" : "section");
  const line = useRotatingLine(status || lines || "Thinking", every).replace(/[.…]+$/, "");
  const known = Number.isFinite(progress);
  const counted = Number.isFinite(step) && Number.isFinite(total);
  const subline = sub || (counted ? `Step ${step} of ${total}` : null);

  if (preset === "page") {
    return (
      <Box
        role="status"
        aria-live="polite"
        data-size="page"
        sx={{
          display: "grid",
          placeItems: "center",
          alignContent: "center",
          justifyItems: "center",
          textAlign: "center",
          gap: "6px",
          minHeight: minHeight || CONTENT_HEIGHT,
          px: "16px",
          ...LIFT,
          ...sx,
        }}
      >
        <HugoFace thinking size={FACE_SIZE.page} sx={{ mb: "18px", animation: `${fadeIn} 320ms ${a4.ease} both`, ...still }} />
        <Box key={line} sx={{ fontSize: { xs: "17px", md: "19px" }, fontWeight: 700, letterSpacing: "-0.015em", color: c.ink, maxWidth: 420, animation: `${fadeIn} 320ms ${a4.ease} both`, ...still }}>
          {line}
        </Box>
        {subline && <Box sx={{ fontSize: "14px", color: c.muted, maxWidth: 420, lineHeight: 1.5 }}>{subline}</Box>}
        {(known || counted) && (
          <ProgressBar value={known ? progress : (step / total) * 100} sx={{ width: "min(100%, 320px)", mt: "16px" }} />
        )}
      </Box>
    );
  }

  if (preset === "inline") {
    return (
      <Box role="status" aria-live="polite" sx={{ display: "inline-flex", alignItems: "center", gap: "10px", fontSize: "13.5px", color: c.muted, minWidth: 0, ...sx }}>
        <HugoFace thinking />
        <Box key={line} component="span" sx={{ animation: `${fadeIn} 320ms ${a4.ease} both`, ...still }}>
          {line}…
        </Box>
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "grid",
        gridTemplateColumns: `${FACE_SIZE.section}px minmax(0,1fr)`,
        alignItems: "center",
        gap: "4px 12px",
        bgcolor: c.card,
        borderRadius: "18px",
        boxShadow: `0 0 0 1px ${c.line}`,
        p: "16px 18px",
        ...sx,
      }}
    >
      <HugoFace thinking size={FACE_SIZE.section} sx={{ gridRow: subline ? "span 2" : undefined }} />
      <Box key={line} sx={{ fontSize: "14.5px", fontWeight: 600, color: c.ink, minWidth: 0, animation: `${fadeIn} 320ms ${a4.ease} both`, ...still }}>
        {line}
      </Box>
      {subline && <Box sx={{ fontSize: "12.5px", color: c.muted }}>{subline}</Box>}
      {(known || counted) && (
        <ProgressBar value={known ? progress : (step / total) * 100} sx={{ gridColumn: "1 / -1", mt: "10px" }} />
      )}
    </Box>
  );
}

HugoThinking.propTypes = {
  lines: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  status: PropTypes.string,
  step: PropTypes.number,
  total: PropTypes.number,
  progress: PropTypes.number,
  sub: PropTypes.node,
  compact: PropTypes.bool,
  size: PropTypes.oneOf(["inline", "section", "page"]),
  every: PropTypes.number,
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  sx: PropTypes.object,
};

/** (e) A known percentage: a 4px violet bar, with an optional label and the % in mono. */
export function ProgressBar({ value = 0, label, showValue = !!label, sx }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <Box sx={{ display: "grid", gap: "8px", minWidth: 0, ...sx }}>
      {(label || showValue) && (
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px", fontSize: "14px", color: c.ink }}>
          {label && <Box component="span" sx={{ fontWeight: 600 }}>{label}</Box>}
          {showValue && (
            <Box component="span" sx={{ fontFamily: tokens.fontFamily.mono, fontSize: "12px", color: c.muted, ml: "auto" }}>
              {pct}%
            </Box>
          )}
        </Box>
      )}
      <Box
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={typeof label === "string" ? label : undefined}
        sx={{ height: 4, borderRadius: "4px", bgcolor: c.track, overflow: "hidden" }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: "4px",
            bgcolor: c.violet,
            transition: `width 420ms ${a4.ease}`,
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />
      </Box>
    </Box>
  );
}

ProgressBar.propTypes = { value: PropTypes.number, label: PropTypes.node, showValue: PropTypes.bool, sx: PropTypes.object };
